<template>
  <div>
    <!-- Invoices Dashboard Section -->
    <div class="dashboard-section">
      <div class="dashboard-header">
        <h2>Invoices Dashboard</h2>
      </div>

      <!-- Dashboard Cards -->
      <div class="cards-container">
        <div
          class="card-wrapper"
          v-for="(card, index) in dashboardCards"
          :key="index"
        >
          <div class="card-content">
            <div class="card-header">
              <div>
                <h3>{{ card.title }}</h3>
                <p>{{ card.description }}</p>
              </div>
              <div
                class="icon-wrapper"
                :style="{ backgroundColor: card.iconBg, color: card.iconColor }"
              >
                <a-icon :class="['fa', card.icon]" />
              </div>
            </div>
            <p class="card-paragraph">
              <span
                v-if="card.spanIcon || card.span"
                :style="{
                  color: card.spanIcon === 'fa-arrow-down' ? '#ef4444' : '#10b981',
                  display: 'inline-flex',
                  alignItems: 'center',
                }"
              >
                <i
                  :class="['fa', card.spanIcon]"
                  style="font-size: 14px; margin-right: 4px"
                ></i>
                {{ card.span }}
              </span>
              {{ card.content }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Search and Filter Section -->
    <div class="search-filter-section">
      <a-row :gutter="16">
        <a-col :span="8">
          <a-input
            v-model="search"
            placeholder="Search by invoice ID or customer name"
            allow-clear
            prefix-icon="search"
            style="width: 100%"
          />
        </a-col>
      </a-row>
    </div>

    <!-- Invoices Table -->
    <div class="table-section">
      <h3>Invoices Overview</h3>
      <a-table
        :columns="columns"
        :data-source="tableData"
        row-key="id"
        :loading="tableLoading"
        :pagination="tablePagination"
        :scroll="{ x: 'max-content' }"
        style="background-color: #fafafa"
        @change="handleTableChange"
      >
        <template slot="date" slot-scope="text">
          {{ formatDate(text) }}
        </template>
        <template slot="amount" slot-scope="text">
          {{ formatAmount(text) }}
        </template>
        <template slot="age" slot-scope="text">
          {{ text }} days
        </template>
        <template slot="disbursementStatus" slot-scope="text">
          <StatusTag :status="text" />
        </template>
        <template slot="repaymentStatus" slot-scope="text">
          <StatusTag :status="text" />
        </template>
        <template slot="actions" slot-scope="text, record">
          <a-dropdown>
            <a-menu slot="overlay" @click="handleActionClick($event, record)">
              <a-menu-item key="viewRepayments">
                <a-icon type="credit-card" />
                View Repayments
              </a-menu-item>
              <a-menu-item key="viewDisbursements">
                <a-icon type="wallet" />
                View Disbursements
              </a-menu-item>
              <a-menu-item key="invoiceDetails">
                <a-icon type="file-text" />
                Invoice Details
              </a-menu-item>
            </a-menu>
            <a-button type="link" style="padding: 0; border: none; box-shadow: none;">
              <a-icon type="more" style="font-size: 16px; color: #666;" />
          </a-button>
          </a-dropdown>
        </template>
      </a-table>
    </div>

    <!-- Repayment Details Drawer -->
    <RepaymentDetailsDrawer
      ref="repaymentDetailsDrawer"
      :visible="showRepaymentDetailsDrawer"
      :repaymentData="selectedRepayment"
      @close="showRepaymentDetailsDrawer = false"
      @repayment-success="onRepaymentSuccess"
    />

    <!-- Invoice Disbursement Details Drawer -->
    <InvoiceDisbursementDetailsDrawer
      ref="invoiceDisbursementDetailsDrawer"
      :visible="showDisbursementDetailsDrawer"
      :invoiceData="selectedDisbursement && selectedDisbursement.invoiceData"
      @close="showDisbursementDetailsDrawer = false"
    />

    <!-- Invoice Details Drawer -->
    <InvoiceDetailsDrawer
      ref="invoiceDetailsDrawer"
      :visible="showInvoiceDetailsDrawer"
      :invoiceData="selectedInvoice"
      @close="showInvoiceDetailsDrawer = false"
    />
  </div>
</template>

<script>
import FinancingAndPaymentsService from "@/api/financing-and-payments.service";
import { formatDate, formatAmount } from "@/utils";
import StatusTag from "../shared-components/StatusTag.vue";
import RepaymentDetailsDrawer from "./RepaymentDetailsDrawer.vue";
import InvoiceDisbursementDetailsDrawer from "./InvoiceDisbursementDetailsDrawer.vue";
import InvoiceDetailsDrawer from "./InvoiceDetailsDrawer.vue";

export default {
  name: "InvoicesTab",
  components: {
    StatusTag,
    RepaymentDetailsDrawer,
    InvoiceDisbursementDetailsDrawer,
    InvoiceDetailsDrawer,
  },
  data() {
    return {
      selectedPeriod: '7d',
      search: "",
      searchDebounceTimer: null,
      filters: {
        // factoringStatus: "Approved",
      },
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        totalCount: 0,
      },
      tableData: [],
      tableLoading: false,
      
      // Drawer state
      showRepaymentDetailsDrawer: false,
      selectedRepayment: null,
      showDisbursementDetailsDrawer: false,
      selectedDisbursement: null,
      showInvoiceDetailsDrawer: false,
      selectedInvoice: null,
      dashboardCards: [
        {
          title: "Total Processed",
          description: "-",
          icon: "fa-file-invoice",
          iconColor: "#3b82f6",
          iconBg: "#dbeafe",
          content: "Total invoice value processed"
        },
        {
          title: "Total Disbursed",
          description: "-",
          icon: "fa-credit-card",
          iconColor: "#10b981",
          iconBg: "#d1fae5",
          content: "Amount disbursed to sellers"
        },
        {
          title: "Total Repayments",
          description: "-",
          icon: "fa-check-circle",
          iconColor: "#26a246",
          iconBg: "#ecfdf5",
          content: "Amount collected from buyers"
        },
        {
          title: "Outstanding Disbursements",
          description: "-",
          icon: "fa-exclamation-triangle",
          iconColor: "#ef4444",
          iconBg: "#fee2e2",
          content: "Pending disbursements"
        },
        {
          title: "Outstanding Repayments",
          description: "-",
          icon: "fa-clock",
          iconColor: "#f59e0b",
          iconBg: "#fef3c7",
          content: "Pending repayments"
        }
      ],
      columns: [
        {
          title: "Invoice ID",
          dataIndex: "id",
          key: "id",
          width: 100,
        },
        {
          title: "Buyer Name",
          dataIndex: "customerBusinessName",
          key: "customerBusinessName",
          width: 150,
        },
        {
          title: "Seller Name",
          dataIndex: "sellerName",
          key: "sellerName",
          width: 150,
        },
        {
          title: "Invoice Date",
          dataIndex: "createdAt",
          key: "createdAt",
          width: 120,
          scopedSlots: { customRender: "date" },
        },
        {
          title: "Factored Value",
          dataIndex: "amountPayable",
          key: "amountPayable",
          width: 130,
          scopedSlots: { customRender: "amount" },
        },
        {
          title: "Disbursed",
          dataIndex: "totalDisbursed",
          key: "totalDisbursed",
          width: 130,
          scopedSlots: { customRender: "amount" },
        },
        {
          title: "Invoice Age",
          dataIndex: "aging",
          key: "aging",
          width: 100,
          scopedSlots: { customRender: "age" },
        },
        {
          title: "Disbursement Status",
          dataIndex: "disbursementStatus",
          key: "disbursementStatus",
          width: 150,
          scopedSlots: { customRender: "disbursementStatus" },
        },
        {
          title: "Repayment Status",
          dataIndex: "repaymentStatus",
          key: "repaymentStatus",
          width: 150,
          scopedSlots: { customRender: "repaymentStatus" },
        },
        {
          title: "Actions",
          key: "actions",
          width: 100,
          scopedSlots: { customRender: "actions" },
        },
      ]
    }
  },
  computed: {
    tablePagination() {
      return {
        current: this.pagination.pageIndex,
        pageSize: this.pagination.pageSize,
        total: this.pagination.totalCount,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        showSizeChanger: false,
      };
    },
  },
  watch: {
    "pagination.pageIndex"() {
      this.fetchData();
    },
    search() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.pagination.pageIndex = 1;
        this.fetchData();
      }, 400);
    },
    filters: {
      handler() {
        this.pagination.pageIndex = 1;
        this.fetchData();
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchData();
    this.fetchStats();
  },
  methods: {
    formatDate,
    formatAmount,
    async fetchData() {
      this.tableLoading = true;
      
      const params = {
        pageIndex: this.pagination.pageIndex,
        pageSize: this.pagination.pageSize,
        factoringStatus: "Approved",
        search: this.search || "",
      };
      
      const response = await FinancingAndPaymentsService.getAllSellerInvoices(params);
      
      if (response.data?.code === 200) {
        const data = response.data.data;
        this.tableData = (data.results || []).map((item) => ({
          id: item.id,
          orderId: item.orderId,
          customerBusinessName: item.customerBusinessName || "N/A",
          sellerName: item.sellerName || "N/A",
          createdAt: item.createdAt,
          dueDate: item.dueDate,
          aging: item.aging || 0,
          fulfilmentType: item.fulfilmentType,
          customerPhoneNumber: item.customerPhoneNumber,
          customerBusinessType: item.customerBusinessType,
          totalOrderValue: item.totalOrderValue || 0,
          discountedValue: item.discountedValue || 0,
          amountPayable: item.amountPayable || 0,
          factoredValue: item.factoredValue || 0,
          customerPaymentTerms: item.customerPaymentTerms,
          factoringstatus: item.factoringstatus,
          totalDisbursed: item.totalDisbursed || 0,
          totalRepaid: item.totalRepaid || 0,
          totalValue: item.discountedValue || 0,
          disbursementStatus: item.disbursementStatus || "Pending",
          repaymentStatus: item.repaymentStatus || "Pending",
        }));
        this.pagination.totalCount = data.totalCount || 0;
      } else {
        this.tableData = [];
        this.pagination.totalCount = 0;
        this.$message.warning("No data received from the server");
      }
      
      this.tableLoading = false;
    },
    async fetchStats() {
        const response = await FinancingAndPaymentsService.getSellerFactoredInvoiceStats();
        
        if (response.data?.code === 200) {
          const stats = response.data.data;
          
          // Update dashboard cards with real data
          this.dashboardCards[0].description = this.formatAmount(stats.totalProcessed);
          this.dashboardCards[1].description = this.formatAmount(stats.totalDisbursed);
          this.dashboardCards[2].description = this.formatAmount(stats.totalCollections);
          this.dashboardCards[3].description = this.formatAmount(stats.outstanding);
          this.dashboardCards[4].description = this.formatAmount(stats.outstandingToBeCollected);
        } else {
          this.$message.warning("Failed to fetch invoice statistics");
        }
    },
    handleTableChange(pagination) {
      if (pagination.current !== this.pagination.pageIndex) {
        this.pagination.pageIndex = pagination.current;
      }
    },
    handleActionClick(event, record) {
      const { key } = event;
      
      switch (key) {
        case 'viewRepayments':
          this.viewRepayments(record);
          break;
        case 'viewDisbursements':
          this.viewDisbursements(record);
          break;
        case 'invoiceDetails':
          this.viewInvoiceDetails(record);
          break;
        default:
          console.log('Unknown action:', key);
      }
    },
    viewRepayments(record) {
      // Map the invoice data to match the repayment data structure expected by RepaymentDetailsDrawer
      this.selectedRepayment = {
        id: record.id,
        invoiceId: record.id,
        invoiceIdFormatted: `INV-${record.id}`,
        buyerName: record.customerBusinessName,
        sellerName: record.sellerName,
        invoiceDate: record.createdAt,
        totalValue: record.totalValue,
        dispensedSoFar: record.totalDisbursed,
        repaid: record.totalRepaid,
        invoiceAge: record.aging,
        repaymentStatus: record.repaymentStatus,
        factoringStatus: "Approved", // Since we're filtering by Approved
        disbursementStatus: record.disbursementStatus,
        repaymentDueDate: record.createdAt, // You might want to calculate this based on terms
      };
      this.showRepaymentDetailsDrawer = true;
    },
    viewDisbursements(record) {
      // Set invoice data for the drawer
      this.selectedDisbursement = {
        invoiceData: {
          id: record.id,
          customerBusinessName: record.customerBusinessName,
          sellerName: record.sellerName,
          amountPayable: record.amountPayable,
          totalDisbursed: record.totalDisbursed,
          disbursementStatus: record.disbursementStatus,
          createdAt: record.createdAt,
        }
      };
      
      this.showDisbursementDetailsDrawer = true;
    },
    viewInvoiceDetails(record) {
      // Set invoice data for the drawer
      this.selectedInvoice = {
        id: record.id,
        orderId: record.orderId,
        createdAt: record.createdAt,
        dueDate: record.dueDate,
        aging: record.aging,
        fulfilmentType: record.fulfilmentType,
        customerBusinessName: record.customerBusinessName,
        sellerName: record.sellerName,
        customerPhoneNumber: record.customerPhoneNumber,
        customerBusinessType: record.customerBusinessType,
        totalOrderValue: record.totalOrderValue,
        discountedValue: record.discountedValue,
        amountPayable: record.amountPayable,
        factoredValue: record.factoredValue,
        customerPaymentTerms: record.customerPaymentTerms,
        factoringstatus: record.factoringstatus,
        disbursementStatus: record.disbursementStatus,
        repaymentStatus: record.repaymentStatus,
        totalDisbursed: record.totalDisbursed,
        totalRepaid: record.totalRepaid,
        repaymentOutstanding: (record.discountedValue - record.totalRepaid).toFixed(2)
      };
      
      this.showInvoiceDetailsDrawer = true;
    },
    onRepaymentSuccess() {
      console.log("Repayment created successfully");
      this.fetchData(); // Refresh the table data
    },
  },
}
</script>

<style scoped>
.dashboard-section {
  margin-bottom: 32px;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.dashboard-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.time-period-selector {
  display: flex;
  align-items: center;
}

.cards-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: flex-start;
  width: 100%;
}

.card-wrapper {
  flex: 0 0 calc(25% - 12px);
  max-width: calc(25% - 12px);
  min-width: 200px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
  transition: all 0.3s ease;
}

.card-wrapper:nth-child(5) {
  flex: 0 0 calc(25% - 12px);
  max-width: calc(25% - 12px);
}

.card-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 120px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  background-color: transparent;
  padding: 0;
  border-bottom: none;
}

.card-header p {
  font-weight: 700;
  font-size: 18px;
  color: #000000;
  margin: 0;
}

.card-header h3 {
  font-size: 0.9em;
  color: #6b7280;
  margin: 0 0 4px 0;
}

.card-paragraph {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  margin: 0;
  line-height: 1.4;
  margin-top: auto;
  color: #6b7280;
}

.icon-wrapper {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 12px;
  flex-shrink: 0;
}

.search-filter-section {
  background: #fff;
  padding: 24px;
  border-radius: 8px;
  margin-bottom: 24px;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.table-section {
  background: #fff;
  padding: 24px;
  border-radius: 8px;
}

.table-section h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
}

/* Responsive */
@media (max-width: 1200px) {
  .card-wrapper {
    flex: 0 0 calc(33.333% - 12px);
    max-width: calc(33.333% - 12px);
  }
  
  .card-wrapper:nth-child(5) {
    flex: 0 0 calc(33.333% - 12px);
    max-width: calc(33.333% - 12px);
  }
}

@media (max-width: 900px) {
  .card-wrapper {
    flex: 0 0 calc(50% - 8px);
    max-width: calc(50% - 8px);
  }
  
  .card-wrapper:nth-child(5) {
    flex: 0 0 calc(50% - 8px);
    max-width: calc(50% - 8px);
  }
}

@media (max-width: 576px) {
  .card-wrapper {
    flex: 0 0 100%;
    max-width: 100%;
  }
  
  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
}
</style> 